import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import type { SpaceRef } from "@etus-mindmap/shared/api";

// Espaço ativo (Pessoal / Org / Time). Persistido em localStorage; alimenta o
// header X-Space de toda chamada autenticada (via useAuthedFetch) e a chave das
// queries de mapas.

const PERSONAL: SpaceRef = { type: "personal", id: null };
const STORAGE_KEY = "etus-mindmap:space";

interface SpaceContextValue {
  activeSpace: SpaceRef;
  setActiveSpace: (s: SpaceRef) => void;
}

const SpaceContext = createContext<SpaceContextValue | null>(null);

// Serialização estável: "personal" | "shared" | "org:{id}" | "team:{id}". É
// exatamente o header X-Space e serve de chave de cache/localStorage.
export function spaceHeader(s: SpaceRef): string {
  return s.type === "personal" || s.type === "shared" ? s.type : `${s.type}:${s.id}`;
}

function readStored(): SpaceRef {
  if (typeof localStorage === "undefined") return PERSONAL;
  const v = localStorage.getItem(STORAGE_KEY);
  if (!v || v === "personal") return PERSONAL;
  if (v === "shared") return { type: "shared", id: null };
  const m = /^(org|team):(\d+)$/.exec(v);
  return m ? { type: m[1] as "org" | "team", id: Number.parseInt(m[2], 10) } : PERSONAL;
}

export function SpaceProvider({ children }: { children: ReactNode }) {
  const [activeSpace, setActiveSpaceState] = useState<SpaceRef>(readStored);
  const value = useMemo<SpaceContextValue>(
    () => ({
      activeSpace,
      setActiveSpace: (s) => {
        setActiveSpaceState(s);
        localStorage.setItem(STORAGE_KEY, spaceHeader(s));
      },
    }),
    [activeSpace],
  );
  return <SpaceContext.Provider value={value}>{children}</SpaceContext.Provider>;
}

export function useActiveSpace(): SpaceContextValue {
  const ctx = useContext(SpaceContext);
  if (!ctx) throw new Error("useActiveSpace must be used inside <SpaceProvider>");
  return ctx;
}
